import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useAccountMutations } from '@/hooks/use-account';
import { ACCOUNT_STATUSES } from '@/constants/feature-labels';
import type { AccountDetail } from '@/types';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  domain: z.string().max(100).optional().or(z.literal('')),
  support_email: z
    .string()
    .email('Enter a valid email')
    .max(100)
    .optional()
    .or(z.literal('')),
  account_status: z.string().optional(),
  sub_domain: z.string().max(100).optional().or(z.literal('')),
  ai_sync_enabled: z.boolean()
});

type FormData = z.infer<typeof schema>;

export function AccountFieldsForm({ account }: { account: AccountDetail }) {
  const { updateAccount } = useAccountMutations();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    values: {
      name: account.name ?? '',
      domain: account.domain ?? '',
      support_email: account.support_email ?? '',
      account_status: account.account_status ?? '',
      sub_domain: account.sub_domain ?? '',
      ai_sync_enabled: account.ai_sync_enabled ?? false
    }
  });

  const status = watch('account_status');
  const aiSync = watch('ai_sync_enabled');

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      await updateAccount({
        name: data.name,
        domain: data.domain || null,
        support_email: data.support_email || null,
        account_status: data.account_status || null,
        sub_domain: data.sub_domain || null,
        ai_sync_enabled: data.ai_sync_enabled
      });
      toast.success('Account updated');
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const field = (
    id: keyof FormData,
    label: string,
    helper?: string,
    type = 'text'
  ) => (
    <div className='space-y-1.5'>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} disabled={!editing} {...register(id)} />
      {helper && <p className='text-xs text-muted-foreground'>{helper}</p>}
      {errors[id] && (
        <p className='text-xs text-destructive'>
          {errors[id]?.message as string}
        </p>
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='space-y-5'>
      <div className='flex justify-end gap-2'>
        {editing ? (
          <>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => {
                reset();
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button type='submit' size='sm' disabled={saving || !isDirty}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        ) : (
          <Button type='button' size='sm' onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>

      <div className='grid gap-4 md:grid-cols-2'>
        {field('name', 'Account name')}
        <div className='space-y-1.5'>
          <Label htmlFor='account_status'>Status</Label>
          <Select
            disabled={!editing}
            value={status || undefined}
            onValueChange={(v) =>
              setValue('account_status', v, { shouldDirty: true })
            }
          >
            <SelectTrigger id='account_status'>
              <SelectValue placeholder='Select a status' />
            </SelectTrigger>
            <SelectContent>
              {ACCOUNT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {field('domain', 'Website domain')}
        {field(
          'support_email',
          'Support email',
          'Where customer replies are sent',
          'email'
        )}
        {field('sub_domain', 'Subdomain', 'e.g. acme (for acme.kim.cc)')}
        <div className='flex items-center justify-between rounded-lg border p-4'>
          <div>
            <p className='text-sm font-medium'>AI sync</p>
            <p className='text-xs text-muted-foreground'>
              Keep this account&apos;s data in sync with the AI system.
            </p>
          </div>
          <Switch
            checked={aiSync}
            disabled={!editing}
            onCheckedChange={(v) =>
              setValue('ai_sync_enabled', v, { shouldDirty: true })
            }
          />
        </div>
      </div>
    </form>
  );
}
